import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "environments/environment";
import { BaseRespone, PaginationResult } from "app/core/models/common.response";
import { SiteMeasurement } from "./site-measurement.types";

@Injectable({
    providedIn: 'root'
})
export class SiteMeasurementsService {
    private _siteMeasurement: BehaviorSubject<SiteMeasurement | null> = new BehaviorSubject(null);
    private _siteMeasurements: BehaviorSubject<SiteMeasurement[] | null> = new BehaviorSubject(null);
    private _builtUpSiteMeasurements: BehaviorSubject<SiteMeasurement[] | null> = new BehaviorSubject(null);
    private _landUpSiteMeasurements: BehaviorSubject<SiteMeasurement[] | null> = new BehaviorSubject(null);
    private _carpetAreaMeasurements: BehaviorSubject<SiteMeasurement[] | null> = new BehaviorSubject(null);
    constructor(private _httpClient: HttpClient) {
    }


    /**
     * Getter for siteMeasurement
     */
    get siteMeasurement$(): Observable<SiteMeasurement> {
        return this._siteMeasurement.asObservable();
    }

    /**
     * Getter for siteMeasurements
     */
    get siteMeasurements$(): Observable<SiteMeasurement[]> {
        return this._siteMeasurements.asObservable();
    }

    /**
     * Getter for siteMeasurements
     */
    get builtUpSiteMeasurements$(): Observable<SiteMeasurement[]> {
        return this._builtUpSiteMeasurements.asObservable();
    }

    get landUpSiteMeasurements$(): Observable<SiteMeasurement[]> {
        return this._landUpSiteMeasurements.asObservable();
    }

    get carpetAreaMeasurements$(): Observable<SiteMeasurement[]> {
        return this._carpetAreaMeasurements.asObservable();
    }

    /**
     * Get siteMeasurements
     */
    getSiteMeasurements(reportId: number): Observable<BaseRespone<SiteMeasurement[]>> {
        return this._httpClient.get<BaseRespone<SiteMeasurement[]>>(`${environment.baseUrl}/Report/GetSiteMeasurementlist/${reportId}`).pipe(
            tap((response) => {
                this._siteMeasurements.next(response.result);
            })
        );
    }

    getLandSiteMeasurements(reportId: number): Observable<BaseRespone<SiteMeasurement[]>> {
        return this._httpClient.get<BaseRespone<SiteMeasurement[]>>(`${environment.baseUrl}/Report/GetSiteMeasurementlist/${reportId}?type=Land Area`).pipe(
            tap((response) => {
                this._landUpSiteMeasurements.next(response.result);
            })
        );
    }

    getBuiltUpSiteMeasurements(reportId: number): Observable<BaseRespone<SiteMeasurement[]>> {
        return this._httpClient.get<BaseRespone<SiteMeasurement[]>>(`${environment.baseUrl}/Report/GetSiteMeasurementlist/${reportId}?type=BuiltUp Area`).pipe(
            tap((response) => {
                this._builtUpSiteMeasurements.next(response.result);
            })
        );
    }

    getCarpetSiteMeasurements(reportId: number): Observable<BaseRespone<SiteMeasurement[]>> {
        return this._httpClient.get<BaseRespone<SiteMeasurement[]>>(`${environment.baseUrl}/Report/GetSiteMeasurementlist/${reportId}?type=Carpet Area`).pipe(
            tap((response) => {
                this._carpetAreaMeasurements.next(response.result);
            })
        );
    }

    /**
      * Create siteMeasurement
      */
    createSiteMeasurement(siteMeasurement: SiteMeasurement, type?: string): Observable<BaseRespone<SiteMeasurement>> {
        switch (type) {
            case 'Land Area':
                return this.landUpSiteMeasurements$.pipe(
                    take(1),
                    switchMap(siteMeasurements => this._httpClient.post<BaseRespone<SiteMeasurement>>(`${environment.baseUrl}/Report/AddSiteMeasurement`, siteMeasurement)
                        .pipe(
                            catchError((error) => {
                                return of(error.error);
                            }),
                            map((response) => {
                                // Return the new product
                                if (response.isSuccess) {
                                    const currentMeasurements = this._landUpSiteMeasurements.getValue();

                                    // Combine new result with existing values
                                    const combined = [response.result, ...currentMeasurements];

                                    // Filter out items where id === 0
                                    const filtered = combined.filter(item => item.id !== 0 && item.type == siteMeasurement.type);

                                    // Update BehaviorSubject with filtered array
                                    this._landUpSiteMeasurements.next(filtered);
                                }
                                return response;
                            })
                        ))
                );
            case 'Builtup Area':
                return this.builtUpSiteMeasurements$.pipe(
                    take(1),
                    switchMap(siteMeasurements => this._httpClient.post<BaseRespone<SiteMeasurement>>(`${environment.baseUrl}/Report/AddSiteMeasurement`, siteMeasurement)
                        .pipe(
                            catchError((error) => {
                                return of(error.error);
                            }),
                            map((response) => {
                                // Return the new product
                                if (response.isSuccess) {
                                    const currentMeasurements = this._builtUpSiteMeasurements.getValue();

                                    // Combine new result with existing values
                                    const combined = [response.result, ...currentMeasurements];

                                    // Filter out items where id === 0
                                    const filtered = combined.filter(item => item.id !== 0 && item.type == siteMeasurement.type);

                                    // Update BehaviorSubject with filtered array
                                    this._builtUpSiteMeasurements.next(filtered);
                                }
                                return response;
                            })
                        ))
                );
            case 'Carpet Area':
                return this.carpetAreaMeasurements$.pipe(
                    take(1),
                    switchMap(siteMeasurements => this._httpClient.post<BaseRespone<SiteMeasurement>>(`${environment.baseUrl}/Report/AddSiteMeasurement`, siteMeasurement)
                        .pipe(
                            catchError((error) => {
                                return of(error.error);
                            }),
                            map((response) => {
                                // Return the new product
                                if (response.isSuccess) {
                                    const currentMeasurements = this._carpetAreaMeasurements.getValue();

                                    // Combine new result with existing values
                                    const combined = [response.result, ...currentMeasurements];

                                    // Filter out items where id === 0
                                    const filtered = combined.filter(item => item.id !== 0 && item.type == siteMeasurement.type);

                                    // Update BehaviorSubject with filtered array
                                    this._carpetAreaMeasurements.next(filtered);
                                }
                                return response;
                            })
                        ))
                );
        }
    }

    addNewMeasurement(reportId: number, selectedValue?: string) {
        switch (selectedValue) {
            case 'Land Area': {
                const currentMeasurements = this._landUpSiteMeasurements.getValue();

                // Create a new measurement object
                const newMeasurement: SiteMeasurement = {
                    id: 0,
                    reportId: reportId,
                    type: selectedValue, // usually 'Land Area'
                    particular: '',
                    width: 0,
                    depth: 0,
                    total: 0,
                    siteArea: 0,
                    siteBuiltUp: 0,
                    siteSuperBuiltUp: 0,
                    orderIndex: currentMeasurements.length + 1, // set order index
                    selected: false,
                    isEditing: true // mark the row as being edited
                };
                this._landUpSiteMeasurements.next([...currentMeasurements, newMeasurement]);
                break;
            }
            case 'Builtup Area': {
                const currentMeasurements = this._builtUpSiteMeasurements.getValue();

                // Create a new measurement object
                const newMeasurement: SiteMeasurement = {
                    id: 0,
                    reportId: reportId,
                    type: selectedValue, // usually 'Land Area'
                    particular: '',
                    width: 0,
                    depth: 0,
                    total: 0,
                    siteArea: 0,
                    siteBuiltUp: 0,
                    siteSuperBuiltUp: 0,
                    orderIndex: currentMeasurements.length + 1, // set order index
                    selected: false,
                    isEditing: true // mark the row as being edited
                };
                this._builtUpSiteMeasurements.next([...currentMeasurements, newMeasurement]);
                break;
            }
            case 'Carpet Area': {
                const currentMeasurements = this._carpetAreaMeasurements.getValue();

                // Create a new measurement object
                const newMeasurement: SiteMeasurement = {
                    id: 0,
                    reportId: reportId,
                    type: selectedValue, // usually 'Land Area'
                    particular: '',
                    width: 0,
                    depth: 0,
                    total: 0,
                    siteArea: 0,
                    siteBuiltUp: 0,
                    siteSuperBuiltUp: 0,
                    orderIndex: currentMeasurements.length + 1, // set order index
                    selected: false,
                    isEditing: true // mark the row as being edited
                };
                this._carpetAreaMeasurements.next([...currentMeasurements, newMeasurement]);
                break;
            }
        }
    }

    cancelLandRow(index: number): void {
        const current = this._landUpSiteMeasurements.getValue();

        if (!current[index].particular?.trim()) {
            const updated = current.filter((_, i) => i !== index);
            this._landUpSiteMeasurements.next(updated);
        } else {
            current[index].isEditing = false;
            this._landUpSiteMeasurements.next([...current]);
        }
    }

    cancelBuiltRow(index: number): void {
        const current = this._builtUpSiteMeasurements.getValue();

        if (!current[index].particular?.trim()) {
            const updated = current.filter((_, i) => i !== index);
            this._builtUpSiteMeasurements.next(updated);
        } else {
            current[index].isEditing = false;
            this._builtUpSiteMeasurements.next([...current]);
        }
    }

    cancelCarpetRow(index: number): void {
        const current = this._carpetAreaMeasurements.getValue();

        if (!current[index].particular?.trim()) {
            const updated = current.filter((_, i) => i !== index);
            this._carpetAreaMeasurements.next(updated);
        } else {
            current[index].isEditing = false;
            this._carpetAreaMeasurements.next([...current]);
        }
    }


    /**
     * Get board
     *
     * @param id
     */
    getSiteMeasurement(id: number): Observable<BaseRespone<SiteMeasurement>> {
        return this._httpClient.get(`${environment.baseUrl}/Report/GetSiteMeasurement/${id}`).pipe(
            catchError((error) => {
                return of(error.error);
            }),
            map(response => response),
            tap(board => this._siteMeasurement.next(board.result))
        );
    }

    /**
    * Get contact by id
    */
    getSiteMeasurementById(id: number): Observable<SiteMeasurement> {
        return this._siteMeasurements.pipe(
            take(1),
            map((siteMeasurements) => {

                // Find the contact
                const siteMeasurement = siteMeasurements.find(item => item.id === id) || null;

                // Update the siteMeasurement
                this._siteMeasurement.next(siteMeasurement);

                // Return the siteMeasurement
                return siteMeasurement;
            }),
            switchMap((siteMeasurement) => {

                if (!siteMeasurement) {
                    return throwError('Could not found siteMeasurement with id of ' + id + '!');
                }

                return of(siteMeasurement);
            })
        );
    }

    /**
     * Update the card
     *
     * @param id
     * @param card
     */
    updateSiteMeasurement(siteMeasurement: SiteMeasurement, type?: string): Observable<BaseRespone<SiteMeasurement>> {
        let subjectToUpdate: BehaviorSubject<SiteMeasurement[]>;

        switch (type) {
            case 'Land Area':
                subjectToUpdate = this._landUpSiteMeasurements;
                break;
            case 'Builtup Area':
                subjectToUpdate = this._builtUpSiteMeasurements;
                break;
            case 'Carpet Area':
                subjectToUpdate = this._carpetAreaMeasurements;
                break;
            default:
                subjectToUpdate = this._siteMeasurements;
                break;
        }

        return subjectToUpdate.pipe(
            take(1),
            switchMap((siteMeasurementsRaw) => {
                const siteMeasurements = Array.isArray(siteMeasurementsRaw) ? siteMeasurementsRaw : [];

                return this._httpClient.put<BaseRespone<SiteMeasurement>>(
                    `${environment.baseUrl}/Report/UpdateSiteMeasurement`,
                    siteMeasurement
                ).pipe(
                    map((siteMeasurementCard) => {
                        if (siteMeasurementCard?.isSuccess) {
                            const updatedMeasurement = siteMeasurementCard.result;
                            const index = siteMeasurements.findIndex(item => item.id === updatedMeasurement.id);

                            if (index !== -1) {
                                siteMeasurements[index] = updatedMeasurement;
                                subjectToUpdate.next([...siteMeasurements]);
                            }
                        }

                        return siteMeasurementCard;
                    }),
                    catchError((error) => {
                        console.error('Error updating site measurement:', error);
                        return of({
                            isSuccess: false,
                            result: null,
                            errors: [error?.message || 'Unknown error occurred']
                        } as BaseRespone<SiteMeasurement>);
                    })
                );
            })
        );
    }


    deleteSiteMeasurement(id: number, type?: string): Observable<BaseRespone<boolean>> {
        const measurementMap: Record<string, {
            observable$: Observable<SiteMeasurement[]>,
            subject: BehaviorSubject<SiteMeasurement[]>
        }> = {
            "Land Area": {
                observable$: this.landUpSiteMeasurements$,
                subject: this._landUpSiteMeasurements
            },
            "Carpet Area": {
                observable$: this.carpetAreaMeasurements$,
                subject: this._carpetAreaMeasurements
            },
            "Builtup Area": {
                observable$: this.builtUpSiteMeasurements$,
                subject: this._builtUpSiteMeasurements
            }
        };

        const measurementEntry = measurementMap[type ?? ""];

        if (!measurementEntry) {
            return throwError(() => new Error("Invalid measurement type"));
        }

        return measurementEntry.observable$.pipe(
            take(1),
            switchMap(siteMeasurements =>
                this._httpClient.delete<BaseRespone<boolean>>(`${environment.baseUrl}/Report/DeleteSiteMeasurement/${id}`).pipe(
                    map(deleted => {
                        const index = siteMeasurements.findIndex(item => item.id === id);
                        if (index !== -1) {
                            siteMeasurements.splice(index, 1);
                            measurementEntry.subject.next([...siteMeasurements]); // emit a new copy
                        }
                        return deleted;
                    })
                )
            )
        );
    }

    deleteAlliteMeasurements(ids: number[]): Observable<BaseRespone<boolean>> {
        const requestBody = { ids: ids };

        return this._httpClient.delete<BaseRespone<boolean>>(`${environment.baseUrl}/Report/DeleteAlliteMeasurements`, {
            body: requestBody
        }).pipe(
            switchMap((deleted: BaseRespone<boolean>) => {
                if (deleted.isSuccess) {

                    this._siteMeasurements.next([]);
                }
                return of(deleted);
            })
        );
    }

    deleteAllSiteMeasurement(ids: number[], type?: string): Observable<BaseRespone<boolean>> {
        const requestBody = { ids };

        const mapping: Record<string, {
            stream$: Observable<SiteMeasurement[]>,
            subject: BehaviorSubject<SiteMeasurement[]>
        }> = {
            'Land Area': {
                stream$: this.landUpSiteMeasurements$,
                subject: this._landUpSiteMeasurements
            },
            'Carpet Area': {
                stream$: this.carpetAreaMeasurements$,
                subject: this._carpetAreaMeasurements
            },
            'Builtup Area': {
                stream$: this.builtUpSiteMeasurements$,
                subject: this._builtUpSiteMeasurements
            }
        };

        const selected = mapping[type || ''];

        if (!selected) {
            const fallback: BaseRespone<boolean> = {
                isSuccess: false,
                message: 'Invalid type',
                errors: ['Invalid type'],
                result: false // ✅ matches result: boolean
            };
            return of(fallback);

        }

        return selected.stream$.pipe(
            take(1),
            switchMap(siteMeasurements =>
                this._httpClient.delete<BaseRespone<boolean>>(
                    `${environment.baseUrl}/Report/DeleteAllSiteMeasurement`,
                    { body: requestBody }
                ).pipe(
                    map((deletedResponse) => {
                        const updatedMeasurements = siteMeasurements.filter(sm => !ids.includes(sm.id));
                        selected.subject.next(updatedMeasurements);
                        return deletedResponse;
                    })
                )
            )
        );
    }
}
